import {
  AUTOMATION_CANCELLATION_CODES,
  AUTOMATION_CANCELLATION_SCHEMA_VERSION,
  type AutomationCancellationDetails,
} from '@/types/automation-cancellation.types';
import type { AutomationExecution } from '@/types/domain.types';
import type { DsnAcceptanceSummary } from '@/types/dsn-return.types';

export type AutomationExecutionTone =
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'neutral';

export type AutomationExecutionIcon =
  | 'success'
  | 'failure'
  | 'running'
  | 'pending'
  | 'retry'
  | 'paused';

export const AUTOMATION_EXECUTION_TONE_CLASSES: Record<
  AutomationExecutionTone,
  string
> = {
  success: 'border-success-200 bg-success-50 text-success-700',
  danger: 'border-error-200 bg-error-50 text-error-700',
  warning: 'border-warning-200 bg-warning-50 text-warning-700',
  info: 'border-primary-200 bg-primary-50 text-primary-700',
  neutral: 'border-neutral-200 bg-neutral-50 text-neutral-700',
};

export interface AutomationExecutionPresentation {
  labelKey: string;
  labelFallback: string;
  descriptionKey?: string;
  descriptionFallback?: string;
  tone: AutomationExecutionTone;
  icon: AutomationExecutionIcon;
  cancellation?: AutomationCancellationDetails;
  unknownCancellation?: boolean;
  requiresAttention?: boolean;
}

const STANDARD_PRESENTATIONS: Record<
  Exclude<AutomationExecution['status'], 'cancelled'>,
  AutomationExecutionPresentation
> = {
  success: {
    labelKey: 'automation.status.success',
    labelFallback: 'Automatisé',
    tone: 'success',
    icon: 'success',
  },
  failed: {
    labelKey: 'automation.status.failed',
    labelFallback: 'Échec',
    tone: 'danger',
    icon: 'failure',
  },
  error: {
    labelKey: 'automation.status.error',
    labelFallback: 'Erreur',
    tone: 'danger',
    icon: 'failure',
  },
  running: {
    labelKey: 'automation.status.running',
    labelFallback: 'En cours',
    tone: 'warning',
    icon: 'running',
  },
  pending: {
    labelKey: 'automation.status.pending',
    labelFallback: 'En attente',
    tone: 'neutral',
    icon: 'pending',
  },
};

const CANCELLATION_PRESENTATIONS: Record<
  string,
  Omit<AutomationExecutionPresentation, 'cancellation'>
> = {
  [AUTOMATION_CANCELLATION_CODES.DSN_BULLETINS_PENDING_RETRY]: {
    labelKey: 'automation.cancellation.bulletinsPending.label',
    labelFallback: 'En attente des bulletins',
    descriptionKey: 'automation.cancellation.bulletinsPending.description',
    descriptionFallback: 'Les bulletins ne sont pas encore prêts.',
    tone: 'info',
    icon: 'retry',
  },
  [AUTOMATION_CANCELLATION_CODES.DSN_CONTROL_BLOCKED_RETRY]: {
    labelKey: 'automation.cancellation.controlBlocked.label',
    labelFallback: 'Contrôle DSN bloquant',
    descriptionKey: 'automation.cancellation.controlBlocked.description',
    descriptionFallback: 'Une anomalie de contrôle doit être examinée.',
    tone: 'danger',
    icon: 'failure',
  },
  [AUTOMATION_CANCELLATION_CODES.OPERATOR_CANCELLED]: {
    labelKey: 'automation.cancellation.operator.label',
    labelFallback: 'Annulée par un administrateur',
    descriptionKey: 'automation.cancellation.operator.description',
    descriptionFallback:
      'Cette exécution a été interrompue volontairement par un administrateur.',
    tone: 'warning',
    icon: 'paused',
  },
  [AUTOMATION_CANCELLATION_CODES.DUPLICATE_SUPPRESSED]: {
    labelKey: 'automation.cancellation.duplicate.label',
    labelFallback: 'Doublon ignoré',
    descriptionKey: 'automation.cancellation.duplicate.description',
    descriptionFallback:
      'Une exécution équivalente existait déjà. Aucune action n’est requise.',
    tone: 'neutral',
    icon: 'paused',
  },
  [AUTOMATION_CANCELLATION_CODES.TECHNICAL_INTERRUPTION]: {
    labelKey: 'automation.cancellation.technical.label',
    labelFallback: 'Interruption technique',
    descriptionKey: 'automation.cancellation.technical.description',
    descriptionFallback:
      'L’interruption technique doit être examinée avant un nouveau lancement.',
    tone: 'danger',
    icon: 'failure',
  },
};

const KNOWN_CANCELLATION_CATEGORIES = [
  'business_prerequisite',
  'operator_action',
  'deduplicated',
  'technical_interruption',
];
const KNOWN_CANCELLATION_INITIATORS = ['system', 'administrator'];

function readCancellation(
  execution: AutomationExecution
): AutomationCancellationDetails | undefined {
  const cancellation = execution.details?.cancellation;

  if (
    !cancellation ||
    typeof cancellation !== 'object' ||
    cancellation.schemaVersion !== AUTOMATION_CANCELLATION_SCHEMA_VERSION ||
    typeof cancellation.code !== 'string' ||
    !KNOWN_CANCELLATION_CATEGORIES.includes(cancellation.category) ||
    !KNOWN_CANCELLATION_INITIATORS.includes(cancellation.initiatedBy) ||
    typeof cancellation.retryPlanned !== 'boolean' ||
    typeof cancellation.requiresAttention !== 'boolean'
  ) {
    return undefined;
  }

  return cancellation;
}

function hasSuccessfulDsnDeposit(execution: AutomationExecution): boolean {
  const netSent = execution.details?.netSent;

  return (
    typeof netSent === 'object' &&
    netSent !== null &&
    'success' in netSent &&
    netSent.success === true
  );
}

const DSN_RETURN_STATUSES = [
  'pending',
  'accepted',
  'accepted_with_anomalies',
  'rejected',
  'unverifiable',
] as const;

export function readDsnAcceptanceSummary(
  execution: AutomationExecution
): DsnAcceptanceSummary | undefined {
  const summary = execution.details?.dsnAcceptance;
  if (
    !summary ||
    typeof summary !== 'object' ||
    !('status' in summary) ||
    !DSN_RETURN_STATUSES.includes(
      summary.status as (typeof DSN_RETURN_STATUSES)[number]
    ) ||
    !('total' in summary) ||
    typeof summary.total !== 'number' ||
    !Number.isFinite(summary.total) ||
    !('availableReports' in summary) ||
    typeof summary.availableReports !== 'number' ||
    !Number.isFinite(summary.availableReports)
  ) {
    return undefined;
  }
  return summary as DsnAcceptanceSummary;
}

export function getAutomationExecutionPresentation(
  execution: AutomationExecution
): AutomationExecutionPresentation {
  if (execution.status !== 'cancelled') {
    if (
      execution.status === 'success' &&
      execution.templateId === 'calculate-dsn' &&
      hasSuccessfulDsnDeposit(execution)
    ) {
      const acceptance = readDsnAcceptanceSummary(execution);
      if (acceptance?.status === 'accepted') {
        return {
          labelKey: 'automation.dsnReturn.accepted.label',
          labelFallback: 'DSN acceptée',
          tone: 'success',
          icon: 'success',
        };
      }
      if (acceptance?.status === 'accepted_with_anomalies') {
        return {
          labelKey: 'automation.dsnReturn.acceptedWithAnomalies.label',
          labelFallback: 'Acceptée · anomalies à vérifier',
          descriptionKey:
            'automation.dsnReturn.acceptedWithAnomalies.description',
          descriptionFallback:
            'Le dépôt est accepté. Un bilan d’anomalies reste à examiner.',
          tone: 'warning',
          icon: 'failure',
          requiresAttention: true,
        };
      }
      if (acceptance?.status === 'rejected') {
        return {
          labelKey: 'automation.dsnReturn.rejected.label',
          labelFallback: 'DSN rejetée',
          descriptionKey: 'automation.dsnReturn.rejected.description',
          descriptionFallback:
            'Consultez le bilan avant toute correction ou nouveau dépôt.',
          tone: 'danger',
          icon: 'failure',
          requiresAttention: true,
        };
      }
      if (acceptance?.status === 'unverifiable') {
        return {
          labelKey: 'automation.dsnReturn.unverifiable.label',
          labelFallback: 'Retour à vérifier',
          descriptionKey: 'automation.dsnReturn.unverifiable.description',
          descriptionFallback:
            'Le retour officiel est temporairement indisponible. Le suivi réessaiera automatiquement.',
          tone: 'warning',
          icon: 'retry',
        };
      }
      return {
        labelKey: 'automation.dsnReturn.depositCompleted.label',
        labelFallback: 'Dépôt effectué',
        descriptionKey: 'automation.dsnReturn.depositCompleted.description',
        descriptionFallback: 'Retour officiel en attente.',
        tone: 'info',
        icon: 'pending',
      };
    }

    return (
      STANDARD_PRESENTATIONS[execution.status] ?? {
        labelKey: 'automation.status.unknown',
        labelFallback: 'État à examiner',
        tone: 'warning',
        icon: 'paused',
      }
    );
  }

  const cancellation = readCancellation(execution);
  const knownPresentation = cancellation
    ? CANCELLATION_PRESENTATIONS[cancellation.code]
    : undefined;

  if (!cancellation || !knownPresentation) {
    return {
      labelKey: 'automation.cancellation.unknown.label',
      labelFallback: 'Annulation à examiner',
      descriptionKey: 'automation.cancellation.unknown.description',
      descriptionFallback:
        'La cause de cette annulation n’est pas disponible ou n’est pas encore reconnue.',
      tone: 'warning',
      icon: 'paused',
      cancellation,
      unknownCancellation: true,
    };
  }

  return {
    ...knownPresentation,
    tone:
      cancellation.requiresAttention &&
      (knownPresentation.tone === 'info' ||
        knownPresentation.tone === 'neutral')
        ? 'warning'
        : knownPresentation.tone,
    cancellation,
  };
}
